/**
 * MIRRTEXT-gated mirroring for BARE rotation-angle fields — DIMENSION's
 * `textRotation`, MULTILEADER's `textRotation` (the MTEXT-content angle,
 * CONTEXT_DATA MTextData group 42 — `multileaderGeometry.ts` feeds it straight
 * into the rendered `mtext-content.rotation`), and a block-attribute's WORLD
 * `overrideRotation` (EATTEDIT absolute rotation override; the render
 * (`attdefFromEntity.ts resolvePlacement`) places a `overridePosition`-carrying
 * ATTRIB at `attr.overrideRotation ?? 0` WITHOUT re-applying the parent INSERT
 * affine — i.e. exactly like a standalone TEXT).
 *
 * Unlike TEXT/MTEXT (`mirrorTextReadable` / `mirrorTextFull` in
 * `geometryTransforms.ts`), these three fields carry NO companion direction
 * vector, generation-flag bits, or OCS extrusion to keep in sync — each is a
 * single CCW-degrees scalar, so the readable/full angle rule
 * (`mirrorAngleMath.ts`) applies directly with no per-type branching.
 *
 * AutoCAD reference: MIRRTEXT governs whether DIMENSION dimension text,
 * MLEADER MTEXT content, and block-ATTRIB text stay readable (0, default) or
 * fully reflect (1) when their containing geometry is mirrored — identical
 * semantics to standalone TEXT/MTEXT, just without the extra per-type fields.
 *
 * Involution note: MIRRTEXT=1 (full reflection, `reflectAngleAcrossAxis`) is
 * ALWAYS self-inverse for a bare scalar (`2α−(2α−θ)=θ` exactly — there is no
 * companion vector to drift the way a custom-direction MTEXT's does), so no
 * undo-restore snapshot is strictly required for that mode. MIRRTEXT=0
 * (readable, `readableAngleAcrossAxis`) is NOT self-inverse (the +180°
 * readability adjustment collapses backward-pointing angles onto their
 * forward twin) — callers that support undo must snapshot the pre-mutation
 * value, exactly like TEXT's `restoreRotations` map
 * (`geometryReducerEdit.ts` / `geometryMutations.ts computeInverse`).
 */

import { MIRROR_AXIS_DEGENERATE_LENSQ } from "./geometryTransforms";
import {
  mirrorAxisAngleDeg,
  normalizeDeg360,
  reflectAngleAcrossAxis,
  readableAngleAcrossAxis,
} from "./mirrorAngleMath";

type Point2D = { x: number; y: number };

/**
 * Shared degenerate-axis guard every bare-rotation-field mirror function in
 * this module applies before transforming — `line1`/`line2` too close to
 * define a mirror axis is a no-op, matching
 * {@link import("./geometryTransforms").mirrorPoint}'s own guard so a
 * degenerate MIRROR command leaves every field — coordinates AND rotation —
 * untouched. Factored out (jscpd) once
 * {@link mirrorFullReflectRelativeAngleField} needed the identical
 * dx/dy/lenSq check.
 *
 * GitHub Codex review PR #558 round 2 (round-7 local read-only follow-up)
 * P2 — exported so `geometryReducerEdit.ts` can gate the Dimension/
 * Multileader/block-attribute HANDEDNESS toggles (`textMirrored`,
 * `overrideGenerationFlags`'s bit-4 XOR) on the SAME degeneracy condition
 * `mirrorPoint`/every rotation field here already uses. Those toggles
 * previously checked ONLY `mirrtext === 1`, so a schema-valid but
 * near-zero-length mirror axis (below `MIRROR_AXIS_DEGENERATE_LENSQ`) left
 * every GEOMETRIC field untouched (this guard's whole point) while still
 * flipping glyph handedness — a visible mutation with no geometric cause,
 * and one `computeInverse` cannot undo either (it returns `null` at the
 * identical threshold, `geometryMutations.ts`'s own guard).
 */
export function isDegenerateMirrorAxis(line1: Point2D, line2: Point2D): boolean {
  const dx = line2.x - line1.x;
  const dy = line2.y - line1.y;
  return dx * dx + dy * dy < MIRROR_AXIS_DEGENERATE_LENSQ;
}

/**
 * Mirror a bare rotation-angle field across `line1` → `line2`, gated by
 * MIRRTEXT (`0` readable / `1` full geometric reflection — same convention as
 * the `mirrtext` command field). Degenerate axis (both points coincide) is a
 * no-op — see {@link isDegenerateMirrorAxis}.
 */
export function mirrorRotationField(
  rotation: number,
  line1: Point2D,
  line2: Point2D,
  mirrtext: number,
): number {
  if (isDegenerateMirrorAxis(line1, line2)) {
    return rotation;
  }
  const axisAngleDeg = mirrorAxisAngleDeg(line1, line2);
  return mirrtext === 1
    ? reflectAngleAcrossAxis(rotation, axisAngleDeg)
    : readableAngleAcrossAxis(rotation, axisAngleDeg).angle;
}

/**
 * GitHub Codex review PR #558 round 2 (round-3 local read-only follow-up)
 * P1 — mirror an ADDITIVE/RELATIVE angle field (a delta from an
 * already-mirrored base angle), as opposed to {@link mirrorRotationField}'s
 * ABSOLUTE angle. Used for `Dimension.textAngleRelativeToDimLine` (DXF
 * group 53), which `dimensionGeometry.ts` adds ON TOP OF a base angle the
 * coordinate transform already re-derives from the mirrored geometry (the
 * DIMTIH/dimline-angle term for Aligned dimensions; a FIXED `base = 0` for
 * every other subtype).
 *
 * Superseded a first-draft (GitHub Codex review PR #558 post-rebase fresh
 * pass P2) `−offset` negation that assumed the per-subtype BASE angle
 * ALWAYS reflects linearly as `base' = 2α − base` under the mirror. That
 * only holds for Aligned dimensions with DIMTIH=false (`base` = the
 * dimline direction angle, `atan2` of the mirrored extension points — a
 * genuine reflected LINE direction). Every other subtype's FIXED
 * `base = 0` before AND after the mirror gives `0 ≠ 2α − 0` unless `α` is
 * a multiple of 90°, so negating the offset alone silently dropped the
 * mirror axis's own contribution for every other axis angle. Concrete
 * counter-example (GitHub Codex review): a horizontal Linear dimension
 * (`base=0`, no override, `offset=0`) mirrored across a VERTICAL axis
 * (`α=90°`) must reflect to 180° (`2·90−0`); the old negation rule left it
 * at 0° (`−0=0`) — a local-Y flip on the WRONG axis instead of the
 * required 180° rotation.
 *
 * Same shape as {@link mirrorReadableRelativeAngleField} (round 2 P1's
 * readable-mode fix) but composes with {@link reflectAngleAcrossAxis}
 * (full reflection) instead of {@link readableAngleAcrossAxis}: want
 * `final' = base' + offset' = reflectAngleAcrossAxis(base + offset,
 * axisAngle)` ⇒ `offset' = reflected − base'`. Verified against the
 * ALREADY-established Aligned+DIMTIH=false worked example (a 30° dimline
 * with a 10° override mirrored across a 90° axis, `base'` re-derives to
 * 150° from the mirrored extension points): `reflectAngleAcrossAxis(30+10,
 * 90) = 140`; `offset' = 140 − 150 = −10 ≡ 350 (mod 360)` — the SAME
 * result the old base-independent negation (`normalizeDeg360(−10) = 350`)
 * already gave for that one subtype/DIMTIH combination where the shortcut
 * happened to hold; this general form now also covers every fixed-base
 * subtype the shortcut silently mishandled.
 *
 * MIRRTEXT=0 (readable) does NOT use this reflection rule — see
 * {@link mirrorReadableRelativeAngleField} for that mode's base-dependent
 * derivation (composing `readableAngleAcrossAxis`'s +180° collapse with an
 * additive delta needs the per-subtype base angle exactly like this
 * function does; only the reflection RULE — full vs. readable — differs).
 */

/**
 * Shared preamble for {@link mirrorFullReflectRelativeAngleField} and
 * {@link mirrorReadableRelativeAngleField} (jscpd dup-gate extraction —
 * both functions guard the degenerate axis identically, resolve the SAME
 * `axisAngleDeg`, and compose the SAME pre-mirror FINAL angle from
 * `base + offset`; only the terminal angle-transform RULE — full reflection
 * vs. readable — differs between them).
 */
function relativeAngleFieldPreamble(
  offset: number,
  baseBeforeMirrorDeg: number,
  line1: Point2D,
  line2: Point2D,
):
  | { degenerate: true }
  | { degenerate: false; axisAngleDeg: number; finalBeforeMirror: number } {
  if (isDegenerateMirrorAxis(line1, line2)) {
    return { degenerate: true };
  }
  return {
    degenerate: false,
    axisAngleDeg: mirrorAxisAngleDeg(line1, line2),
    finalBeforeMirror: baseBeforeMirrorDeg + offset,
  };
}
export function mirrorFullReflectRelativeAngleField(
  offset: number,
  baseBeforeMirrorDeg: number,
  baseAfterMirrorDeg: number,
  line1: Point2D,
  line2: Point2D,
): number {
  const pre = relativeAngleFieldPreamble(offset, baseBeforeMirrorDeg, line1, line2);
  if (pre.degenerate) return offset;
  const reflectedFinal = reflectAngleAcrossAxis(pre.finalBeforeMirror, pre.axisAngleDeg);
  return normalizeDeg360(reflectedFinal - baseAfterMirrorDeg) || 0;
}

/**
 * GitHub Codex review PR #558 (round 2) P1 — the MIRRTEXT=0 (readable)
 * counterpart of {@link mirrorFullReflectRelativeAngleField}'s MIRRTEXT=1
 * reflection. "Readable" is a property of the FINAL COMPOSED angle (`base +
 * offset`, where `base` is the per-subtype dimline-derived term
 * `dimensionSubtypeBaseAngleDeg` computes — @dxf-ai-editor/drawing-model),
 * not of the offset alone: `readableAngleAcrossAxis`'s +180° collapse
 * depends on the SIGN of the fully-reflected FINAL angle, which requires
 * knowing `base` before AND after the mirror — the caller must supply both
 * (computed from the entity's pre-mirror and post-mirror
 * `extLine1Point`/`extLine2Point` via the SAME shared
 * `dimensionSubtypeBaseAngleDeg` the renderer/bounds gate use, so this
 * never re-derives a parallel base-angle formula).
 *
 * Derivation: want `final' = base' + offset' = readableAngleAcrossAxis(base
 * + offset, axisAngle).angle` ⇒ `offset' = readableFinal − base'`. Verified
 * against the GitHub Codex review's own worked example: a horizontal
 * Aligned dimension (`base=0`) with a zero offset, mirrored across a
 * vertical axis (`axisAngle=90°`) — the dimline recomputes `base'=180`
 * (`atan2` of the now-reversed extension-line vector); `readableAngleAcrossAxis
 * (0, 90).angle = 0` (the ORIGINAL angle 0° reflects to 180°, which reads
 * backward, so the readable rule flips it back to 0°); `offset' = 0 − 180 =
 * −180 → normalizeDeg360 → 180`, giving `final' = base'(180) + offset'(180)
 * = 360° ≡ 0°` — readable, matching the pre-mirror angle exactly (the
 * previous, buggy `offset'=0` gave `final'=180`, upside-down — the exact
 * bug the finding reported).
 */
export function mirrorReadableRelativeAngleField(
  offset: number,
  baseBeforeMirrorDeg: number,
  baseAfterMirrorDeg: number,
  line1: Point2D,
  line2: Point2D,
): number {
  const pre = relativeAngleFieldPreamble(offset, baseBeforeMirrorDeg, line1, line2);
  if (pre.degenerate) return offset;
  const readableFinal = readableAngleAcrossAxis(
    pre.finalBeforeMirror,
    pre.axisAngleDeg,
  ).angle;
  return normalizeDeg360(readableFinal - baseAfterMirrorDeg) || 0;
}

const DEG_PER_RAD = 180 / Math.PI;
const RAD_PER_DEG = Math.PI / 180;

/**
 * RADIANS-aware sibling of {@link mirrorRotationField}, for the ONE bare-
 * rotation field that does NOT follow the DXF-angle-fields-are-degrees
 * convention: MULTILEADER's `textRotation` (entity-level flat field AND its
 * `annotativeContexts[].textRotation` / `secondaryContexts[].context.
 * mTextData.rotation` siblings). GitHub Codex review PR #558 (post-rebase
 * fresh pass) — confirmed radians by TWO independent sources:
 *
 *   1. `packages/command-registry/src/commands/annotation.ts` documents the
 *      `update-multileader-text-rotation` command's `textRotation` param as
 *      "New text rotation angle in radians" (ru/en both) — and
 *      `geometryReducer.ts`'s `geometry/update-multileader-text-rotation`
 *      case writes that value STRAIGHT into `entity.textRotation` with no
 *      conversion, so the field IS whatever unit the command promises.
 *   2. `packages/drawing-model/src/dxfBridge.mleaderConformance.sl5.test.ts`
 *      (`SL-5 #432 r8 F1`) sets `ml.geometry.textRotation =
 *      0.5235987755982988` (= Math.PI/6, 30°) and asserts the byte-faithful
 *      writer materializes CONTEXT_DATA MTextData group 42 with that EXACT
 *      raw value, verified `ezdxf`-conformant (0 errors/fixes, AC1032) — i.e.
 *      the model field is written to DXF group 42 with NO unit conversion,
 *      and ezdxf's own `MTextData.rotation` (group 42) is documented in
 *      radians. This matches real MTEXT's own group-50 anomaly (`geometry
 *      Transforms.ts`'s "MTEXT HANDEDNESS" doc): unlike TEXT/DIMENSION/
 *      block-ATTRIB, whose angle fields are decimal degrees, an MTEXT-shaped
 *      content angle is radians end-to-end (model ⇔ DXF, no conversion at
 *      either boundary) — three.js also consumes radians directly, so the
 *      render path never converts either.
 *
 * `mirrorRotationField` / `mirrorAngleMath.ts` operate in DEGREES throughout
 * (mod-360 normalization, `reflectAngleAcrossAxis`/`readableAngleAcrossAxis`).
 * Feeding a radians value (e.g. 0.5236 for 30°) straight in treats it as if
 * it were ~0.52 DEGREES — the degrees-normalized DEGREES output then gets
 * written back into the radians field verbatim, corrupting both the live
 * render and the exported DXF group-42 angle (a 30° MLeader text mirrored
 * across a vertical axis wrongly computes ~179.476, as if the source were
 * 0.5236° instead of 0.5236 rad ≈ 30°). This wrapper converts radians→degrees
 * before delegating to {@link mirrorRotationField}, then converts the
 * degrees result back to radians — the periodicity is consistent either way
 * (mod 360° ⇔ mod 2π rad), so no precision/range is lost by round-tripping
 * through degrees for the shared angle math.
 *
 * GitHub Codex review PR #558 round 2 (round-16 local read-only follow-up)
 * P2 — "Return radians unchanged for a degenerate mirror axis". A
 * degrees→radians round-trip is NOT bit-identity for most doubles (e.g.
 * `0.1 * DEG_PER_RAD * RAD_PER_DEG !== 0.1`, off by ~2e-17) — for a
 * schema-valid but near-zero-length axis, `mirrorRotationField` ITSELF
 * correctly no-ops (returns the degrees input verbatim via
 * {@link isDegenerateMirrorAxis}'s guard), but this wrapper's OWN
 * round-trip through degrees still perturbs the value by that ULP-level
 * drift — a "no geometric change" MIRROR would still silently mutate the
 * MLeader's primary AND every annotative context's `textRotation` by an
 * un-undoable amount (`computeInverse` returns `null` at the IDENTICAL
 * degenerate threshold — round-7's own doc — so this drift carries no undo
 * snapshot at all). Short-circuit BEFORE any conversion, mirroring the
 * degenerate guard every other function in this module already applies
 * first.
 */
export function mirrorRotationFieldRadians(
  rotationRad: number,
  line1: Point2D,
  line2: Point2D,
  mirrtext: number,
): number {
  if (isDegenerateMirrorAxis(line1, line2)) {
    return rotationRad;
  }
  const resultDeg = mirrorRotationField(
    rotationRad * DEG_PER_RAD,
    line1,
    line2,
    mirrtext,
  );
  return resultDeg * RAD_PER_DEG;
}

/**
 * MIRRTEXT-gated local-frame pitch offset for a multiline ATTDEF/ATTRIB
 * value's line `i` — the step-down `distance` units along the text's OWN
 * LOCAL reading-down direction, rotated by the text's LOCAL `rotationDeg`
 * (CCW, DXF convention). At `rotationDeg === 0, mirrored === false` this
 * reduces to `{ x: 0, y: -distance }`.
 *
 * jscpd dup-gate extraction (GitHub Codex review PR #558 round 2, round-11
 * local read-only follow-up) — this was independently duplicated near-
 * identically in BOTH the typed scene3d oracle (`attdefFromEntity.ts`) and
 * the PDF converter (`insertAttributes.ts`): the SAME one domain truth (how
 * a mirrored multiline block's line-stack pitch offset forms in the text's
 * local frame, BEFORE the caller's own affine/rotation applies it), so it
 * belongs in `command-engine` where both already import MIRRTEXT-adjacent
 * constants (`TEXT_GEN_FLAG_UPSIDE_DOWN`/`TEXT_GEN_FLAG_BACKWARD`) from.
 *
 * `mirrored` handles the GitHub Codex review PR #558 round 2 (round-11)
 * P2 — "Reflect multiline attribute baselines with the glyphs": a
 * MIRRTEXT=1-mirrored run's local-Y flip (`applyMirrorScale`'s local-Y
 * scale, matching `generationFlags` bit 4 / a mirrored Dimension/
 * Multileader's `textMirrored`) applies to the WHOLE local text-block
 * frame, not just each individual glyph — a multi-line stack must ALSO
 * reflect around line 0's own anchor, so line `i` sits ABOVE (not below)
 * for a mirrored run, in the CORRECT (reversed) order. Composition:
 * mirroring flips the LOCAL pre-rotation offset's Y component
 * (`(0,-distance) → (0,+distance)`) BEFORE the SAME rotation applies —
 * algebraically this multiplies the ENTIRE unmirrored result by
 * `mirrorSign` (both x and y), reducing to the original formula when
 * `mirrored` is false.
 *
 * Callers thread this through their OWN affine/block-transform machinery
 * (`applyLinear` for the PDF converter, the typed placement's own rotation
 * composition for the scene3d oracle) rather than this helper applying any
 * outer transform itself — a non-uniformly scaled INSERT must space the
 * lines by the correct per-axis pitch, not by the final (post-scale)
 * height along the final rotation, which is only correct under uniform
 * scale.
 */
export function rotateLineStackOffset(
  distance: number,
  rotationDeg: number,
  mirrored: boolean,
): { x: number; y: number } {
  const rad = (rotationDeg * Math.PI) / 180;
  const mirrorSign = mirrored ? -1 : 1;
  return {
    x: mirrorSign * distance * Math.sin(rad),
    y: mirrorSign * -distance * Math.cos(rad),
  };
}
